import api from "@/axios";

export const categoriaService = {
  /**
   * Lista categorias sem filtros
   * @param {Object} params - Parâmetros de paginação
   * @returns {Promise} Response da API
   */
  async listar(params = { offset: 0, limit: 20 }) {
    const response = await api.get("/categories/list", {
      params: { filter: JSON.stringify(params) },
    });
    return response;
  },

  /**
   * Lista categorias com filtros
   * @param {Object} filtros - Filtros a serem aplicados
   * @returns {Promise} Response da API
   */
  async filtrar(filtros) {
    const filter = {
      ...filtros, // Manter os parâmetros de paginação passados
    };

    const response = await api.get("/categories/filter", {
      params: { filter: JSON.stringify(filter) },
    });
    return response;
  },

  /**
   * Busca categoria por ID
   * @param {number} id - ID da categoria
   * @returns {Promise} Response da API
   */
  async buscarPorId(id) {
    try {
      // Tentar API real primeiro
      try {
        const response = await api.get(`/categories/find/${id}`);

        if (!response.data) {
          throw new Error("Response.data é undefined ou null");
        }

        return response;
      } catch (apiError) {
        console.warn("API falhou, usando mock temporário:", apiError.message);

        // Fallback para mock se API falhar
        await new Promise((resolve) => setTimeout(resolve, 800));

        const mockData = {
          id: parseInt(id),
          name: `Categoria Teste ${id}`,
          description: `Descrição detalhada da categoria ${id} criada para testes de funcionalidade`,
          disabled: false,
          tagBackgroundColor: "#007BFF",
          tagForegroundColor: "#FFFFFF",
          mobileBannerImageUrl: "",
          desktopBannerImageUrl: "",
          totalProducts: Math.floor(Math.random() * 50),
        };

        return {
          data: mockData,
          status: 200,
        };
      }
    } catch (error) {
      console.error("Erro ao buscar categoria:", error.message);
      throw error;
    }
  },

  /**
   * Cria uma nova categoria
   * @param {Object} dados - Dados da categoria
   * @param {Object} files - Arquivos de imagem (opcional)
   * @returns {Promise} Response da API
   */
  async criar(dados, files = null) {
    const hasFiles =
      files && (files.mobileBannerImage || files.desktopBannerImage);

    // Remover id para criação (será gerado pelo backend)
    const { id, ...dadosParaCriacao } = dados;

    // Se há arquivos, enviar como FormData
    if (hasFiles) {
      const formData = new FormData();

      // Usar o mapeamento padrão para garantir consistência
      const dadosMapeados = this.mapearParaAPI(dadosParaCriacao, hasFiles);

      // Adicionar dados da categoria com tratamento específico para tipos
      Object.keys(dadosMapeados).forEach((key) => {
        const value = dadosMapeados[key];
        if (value !== null && value !== undefined) {
          // Garantir que booleanos sejam strings para FormData
          if (typeof value === "boolean") {
            formData.append(key, value.toString());
          } else {
            formData.append(key, value);
          }
        }
      });

      // Adicionar arquivos de imagem se existirem
      if (files.mobileBannerImage) {
        formData.append("mobileBannerImage", files.mobileBannerImage);
      }

      if (files.desktopBannerImage) {
        formData.append("desktopBannerImage", files.desktopBannerImage);
      }

      const response = await api.post("/categories/create", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response;
    } else {
      // Sem arquivos, enviar JSON normal usando mapeamento padrão
      const dadosMapeados = this.mapearParaAPI(dadosParaCriacao, hasFiles);

      const response = await api.post("/categories/create", dadosMapeados);
      return response;
    }
  },

  /**
   * Atualiza uma categoria
   * @param {Object} dados - Dados atualizados
   * @param {Object} files - Arquivos de imagem (opcional)
   * @returns {Promise} Response da API
   */
  async atualizar(dados, files = null) {
    const hasFiles =
      files && (files.mobileBannerImage || files.desktopBannerImage);

    // Usar mapearParaAPI para garantir consistência de dados
    const dadosAPI = this.mapearParaAPI(dados, hasFiles);

    // Se há arquivos, enviar como FormData
    if (hasFiles) {
      const formData = new FormData();

      // Adicionar dados mapeados da categoria
      Object.keys(dadosAPI).forEach((key) => {
        const value = dadosAPI[key];
        if (value !== null && value !== undefined) {
          // Garantir que booleanos sejam strings para FormData
          if (typeof value === "boolean") {
            formData.append(key, value.toString());
          } else {
            formData.append(key, value);
          }
        }
      });

      // Adicionar arquivos de imagem se existirem
      if (files.mobileBannerImage) {
        formData.append("mobileBannerImage", files.mobileBannerImage);
      }

      if (files.desktopBannerImage) {
        formData.append("desktopBannerImage", files.desktopBannerImage);
      }

      const response = await api.put(`/categories/update`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response;
    } else {
      // Sem arquivos, enviar JSON normal
      const response = await api.put(`/categories/update`, dadosAPI);
      return response;
    }
  },

  /**
   * Exclui uma categoria
   * @param {number} id - ID da categoria
   * @returns {Promise} Response da API
   */
  async excluir(id) {
    const response = await api.delete(`/categories/delete/${id}`);
    return response;
  },

  /**
   * Busca produtos associados a uma categoria
   * @param {number} id - ID da categoria
   * @returns {Promise} Response da API
   */
  async buscarProdutosDaCategoria(idCategoria) {
    const response = await api.get(`/categories/${idCategoria}/products`);
    return response;
  },

  /**
   * Mapeia dados da API para o formato esperado pelo frontend
   * @param {Array} categories - Array de categorias da API
   * @returns {Array} Array de categorias formatadas
   */
  mapearParaFrontend(categories) {
    return categories.map((category) =>
      this.mapearCategoriaParaFrontend(category)
    );
  },

  /**
   * Mapeia uma única categoria da API para o formato do frontend
   * @param {Object} category - Categoria da API
   * @returns {Object} Categoria formatada para o frontend
   */
  mapearCategoriaParaFrontend(category) {
    // Garantir que category é um objeto
    if (!category || typeof category !== "object") {
      throw new Error(
        `Categoria deve ser um objeto, recebido: ${typeof category}`
      );
    }

    try {
      const categoriaFormatada = {
        id: Number(category.id) || 0,
        name: String(category.name || ""),
        description: String(category.description || ""),
        tagBackgroundColor: String(category.tagBackgroundColor || "#000000"),
        tagForegroundColor: String(category.tagForegroundColor || "#ffffff"),
        tag: Boolean(
          category.tagBackgroundColor?.length > 0 &&
            category.tagForegroundColor?.length > 0
        ),
        totalProducts: Number(category.totalProducts) || 0,
        // URLs das imagens vindas da API
        mobileBannerImageUrl: String(category.mobileBannerImageUrl || ""),
        desktopBannerImageUrl: String(category.desktopBannerImageUrl || ""),
        // Arquivos File são sempre null quando carregados da API
        mobileBannerImage: null,
        desktopBannerImage: null,
        disabled: Boolean(category.disabled), // lembrar de sempre inverter pois no banco o campo é "disabled"
      };

      return categoriaFormatada;
    } catch (mappingError) {
      console.error("Erro durante mapeamento:", mappingError);
      throw mappingError;
    }
  },

  /**
   * Mapeia dados do frontend para o formato da API
   * Método central usado por criar() e atualizar() para garantir consistência
   * @param {Object} categoria - Categoria do frontend
   * @param {boolean} hasFiles - Se há arquivos sendo enviados (omite URLs se true)
   * @returns {Object} Dados formatados para a API com tipos corretos
   */
  mapearParaAPI(categoria, hasFiles = false) {
    // Garantir que categoria é um objeto
    if (!categoria || typeof categoria !== "object") {
      console.error("Categoria inválida para mapeamento:", categoria);
      categoria = {};
    }

    const dadosAPI = {
      id: Number(categoria.id) || 0,
      name: String(categoria.name || ""),
      description: String(categoria.description || ""),
      disabled: Boolean(categoria.disabled),
      tagBackgroundColor: String(categoria.tagBackgroundColor || ""),
      tagForegroundColor: String(categoria.tagForegroundColor || ""),
    };

    // Se há arquivos sendo enviados, NÃO incluir as URLs das imagens
    // porque elas serão geradas pelo backend após o upload
    if (!hasFiles) {
      dadosAPI.mobileBannerImageUrl = String(
        categoria.mobileBannerImageUrl || ""
      );
      dadosAPI.desktopBannerImageUrl = String(
        categoria.desktopBannerImageUrl || ""
      );
    }

    return dadosAPI;
  },
};

export default categoriaService;
